import { ORPCError } from '@orpc/client';

export const isUnauthorizedError = (error: unknown): boolean => error instanceof ORPCError && error.status === 401;

export const isBadRequestError = (error: unknown): boolean => error instanceof ORPCError && error.status === 400;

/**
 * The parsed body of a failed call (`{ message, blockers }` for a blocked KPI delete, `{ message, warnings }` for a
 * formula needing confirmation, `{ message, conflict }` for an overlapping target), or `undefined`. The service
 * answers with Nest's error bodies, which oRPC's OpenAPI link does not recognise as oRPC errors and hands over
 * wrapped as `data.body`; an oRPC-shaped error carries the body as `data` itself. Both are read here, in one place.
 */
export const errorData = (error: unknown): unknown => {
  if (!(error instanceof ORPCError)) {
    return undefined;
  }

  const data: unknown = error.data;

  return typeof data === 'object' && data !== null && 'body' in data ? (data as { body: unknown }).body : data;
};

/** Prefers the service's own message (a 400 names what was wrong) over a generic fallback. */
export const serverMessage = (error: unknown): string | null => {
  if (!(error instanceof ORPCError)) {
    return null;
  }

  const data = errorData(error) as { message?: unknown } | undefined;

  if (data && typeof data.message === 'string' && data.message.trim() !== '') {
    return data.message;
  }

  return typeof error.message === 'string' && error.message.trim() !== '' ? error.message : null;
};

export const isConflictError = (error: unknown): boolean => error instanceof ORPCError && error.status === 409;

export const isForbiddenError = (error: unknown): boolean => error instanceof ORPCError && error.status === 403;

/** Optional text that must be clearable on a PATCH: an emptied input sends an explicit `null`. */
export const clearableTextFieldOptions = {
  setValueAs: (value: unknown): string | null => {
    if (typeof value !== 'string') {
      return value === null || value === undefined ? null : String(value);
    }

    const trimmed = value.trim();

    return trimmed === '' ? null : trimmed;
  },
} as const;

/** A `<select>` whose empty option means "none": `''` becomes `null`. */
export const nullableSelectOptions = {
  setValueAs: (value: unknown): string | null => (value === '' || value === null || value === undefined ? null : String(value)),
} as const;
